import {
  CUSTOM_INVITE_CODE_SUFFIX_MAX_LENGTH,
  CUSTOM_INVITE_CODE_SUFFIX_MIN_LENGTH,
} from '@nadoutong/contracts';
import { useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import { inviteCodePrefixLabel } from '../lib/customInviteCode.ts';
import { FormField, Input } from '../ui/index.ts';

/**
 * Suffix box for a custom invite code. The brand prefix is fixed and rendered
 * beside the input; the operator only types the part after `<PREFIX>-`.
 * When `brand` is null the prefix is omitted — the caller has not learned a
 * brand yet (a lead whose salesperson list is still empty).
 */
interface CustomInviteCodeFieldProps {
  readonly fieldId: string;
  readonly testId: string;
  readonly label: string;
  readonly hintKey: I18nKey;
  readonly brand: string | null;
  readonly value: string;
  readonly onChange: (next: string) => void;
  readonly error: string | null;
  readonly disabled?: boolean;
}

export function CustomInviteCodeField({
  fieldId,
  testId,
  label,
  hintKey,
  brand,
  value,
  onChange,
  error,
  disabled = false,
}: CustomInviteCodeFieldProps): JSX.Element {
  const t = useT();
  const prefix = inviteCodePrefixLabel(brand);
  return (
    <FormField
      label={label}
      fieldId={fieldId}
      hint={t(hintKey, {
        min: CUSTOM_INVITE_CODE_SUFFIX_MIN_LENGTH,
        max: CUSTOM_INVITE_CODE_SUFFIX_MAX_LENGTH,
      })}
      error={error}
    >
      <div className="flex items-center gap-2">
        {prefix !== null ? (
          <span
            className="shrink-0 font-mono text-dense text-paper-mid"
            data-testid={`${testId}-prefix`}
          >
            {prefix}
          </span>
        ) : null}
        <Input
          id={fieldId}
          value={value}
          onChange={(event) => onChange(event.currentTarget.value.toUpperCase())}
          disabled={disabled}
          invalid={error !== null}
          mono
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          aria-describedby={error !== null ? `${fieldId}-error` : undefined}
          data-testid={testId}
        />
      </div>
    </FormField>
  );
}
